import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import FinishSignInPage from './pages/FinishSignInPage';
import OnboardingPage from './pages/OnboardingPage';
import ArtChatPage from './pages/ArtChatPage';
import CirclesPage from './pages/CirclesPage';
import CircleDetailPage from './pages/CircleDetailPage';
import CreateJoinCirclePage from './pages/CreateJoinCirclePage';
import JoinByLinkPage from './pages/JoinByLinkPage';
import PricingPage from './pages/PricingPage';
import BulkCirclesPage from './pages/BulkCirclesPage';
import SessionExpiredModal from './components/SessionExpiredModal';
import UpdateBanner from './components/UpdateBanner';
import { useVersionCheck } from './hooks/useVersionCheck';

function AppRoutes() {
  const { currentUser, loading, sessionExpired } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { hasUpdate, dismissUpdate } = useVersionCheck();

  const handleRefresh = () => {
    window.location.reload();
  };

  useEffect(() => {
    const handleAuthExpired = () => {
      setShowAuthModal(true);
    };
    
    window.addEventListener('artpingpong-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('artpingpong-auth-expired', handleAuthExpired);
  }, []);
  
  useEffect(() => {
    if (sessionExpired) {
      setShowAuthModal(true);
    }
  }, [sessionExpired]);

  // Allow /finishSignIn and /join/:code routes without authentication check
  const isFinishSignInRoute = window.location.pathname === '/finishSignIn';
  const isJoinRoute = window.location.pathname.startsWith('/join/');
  
  if (loading) return <SplashScreen />;
  
  // Show login page if not authenticated and not on finishSignIn or join route
  if (!currentUser && !sessionExpired && !showAuthModal && !isFinishSignInRoute && !isJoinRoute) {
    return <LoginPage />;
  }

  // Handle onboarding for authenticated users
  const hasOnboarded = localStorage.getItem('app_onboarding_completed') === 'true';
  if (!sessionExpired && !showAuthModal && currentUser && !hasOnboarded && !isFinishSignInRoute) {
    return <OnboardingPage />;
  }

  return (
    <SubscriptionProvider>
      {hasUpdate && <UpdateBanner onRefresh={handleRefresh} onDismiss={dismissUpdate} />}
      {showAuthModal && <SessionExpiredModal />}
      <Routes>
        <Route path="/finishSignIn" element={<FinishSignInPage />} />
        <Route path="/join/:code" element={<JoinByLinkPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/art" replace />} />
          <Route path="/art" element={<ArtChatPage />} />
          <Route path="/circles" element={<CirclesPage />} />
          <Route path="/circles/new" element={<CreateJoinCirclePage />} />
          <Route path="/circles/:id" element={<CircleDetailPage />} />
          <Route path="/admin/bulk-circles" element={<BulkCirclesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/art" replace />} />
      </Routes>
    </SubscriptionProvider>
  );
}

function SplashScreen() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100dvh', background: '#fffbeb',
    }}>
      <div style={{ textAlign: 'center' }}>
        <img src="/AppLogoStacked.svg" alt="Art Ping Pong" style={{ height: '140px', width: 'auto' }} />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
